import React, { useState, useEffect, useContext, useRef } from 'react'
import './Users.css'
import { Context } from '../../context/Context'
import { useAccess } from '../../access/hooks/useAccess'
import { BtwnStores } from './btwnStores/BtwnStores';
import { translations } from "../../utils/lang";
import { fetchUsersAPI, updateUserAPI, patchBetweenUserAPI, signupUserAPI, setGlobalAccessAPI } from "../../utils/api/users";
import { ASSIGNABLE_ROLES } from '../../access/core/roles';
import { isGlobalEligibleRole } from '../../access/core/scope';
import {
    Button,
    Input,
    Select,
    Checkbox,
    Switch,
    ActionDialog,
    NoticeBox,
    SearchCheckList,
    Pencil,
    XCircle,
} from '../ui';

// The assignable roles live in the access layer (they must match the backend's role enum).
const availableAccess = ASSIGNABLE_ROLES;

const baseUser = {
    name: '',
    contactEmail: '',
    city: '',
    phone: '',
    email: '',
    password: '',
    role: '',
    isAvailable: false,
    approved: false,
}

// Only these fields are edited here. Sending the whole loaded user back also
// resent `between`/`assignedBranch` (reverting permission changes saved since
// the form opened) and the stored password hash.
const EDITABLE_FIELDS = ['name', 'contactEmail', 'city', 'phone', 'role', 'isAvailable', 'approved'];
const CREATE_FIELDS = [...EDITABLE_FIELDS, 'email', 'password'];

const pick = (source, fields) =>
    fields.reduce((result, field) => {
        if (source[field] !== undefined) result[field] = source[field];
        return result;
    }, {});

// 403 is a permission answer: it never signs the administrator out (only a rejected session does).
const isAuthError = (error) => error?.status === 401;

const TEXT = {
    ar: {
        openForm: "إضافة مستخدم",
        closeForm: "إغلاق النموذج",
        editUser: (name) => `تعديل: ${name}`,
        showPermissions: (name) => `أذونات ${name}`,
        approvedLabel: "معتمد",
        approved: "معتمد",
        notApproved: "غير معتمد",
        filterBy: (column) => `بحث في ${column}`,
        loading: "جارٍ التحميل...",
        empty: "لا توجد بيانات.",
        loadFailed: "تعذر تحميل المستخدمين.",
        retry: "إعادة المحاولة",
        saving: "جارٍ الحفظ...",
        saveFailed: "تعذر حفظ المستخدم. حاول مرة أخرى.",
        permissionsFailed: "تعذر حفظ الأذونات. حاول مرة أخرى.",
        missing: (names) => `أكمل الحقول المطلوبة: ${names}`,
        contactEmailHint: "بريد إلكتروني حقيقي لاستعادة كلمة المرور والإشعارات. اتركه فارغًا إن لم يكن لدى الشخص بريد شخصي، وتتم الاستعادة حينها عبر المسؤول.",
        invalidContactEmail: "بريد التواصل غير صالح.",
        globalAccess: {
            label: "الوصول لجميع المطاعم",
            hint: "عند التفعيل يصل المستخدم إلى جميع المطاعم ضمن صلاحيات دوره فقط، ولا تتغير صلاحيات الدور.",
            on: "الوصول لجميع المطاعم مفعّل",
            off: "الوصول للمطاعم المسندة فقط",
            enableTitle: "تفعيل الوصول لجميع المطاعم",
            disableTitle: "إلغاء الوصول لجميع المطاعم",
            enableBody: (name) => `سيتمكن ${name} من الوصول إلى جميع المطاعم الحالية والمستقبلية ضمن صلاحيات دوره. تبقى المطاعم المسندة محفوظة.`,
            disableBody: (name, count) => `سيعود ${name} إلى المطاعم المسندة له فقط (${count}). قد يفقد فورًا الوصول إلى مطاعم أخرى كان يراها.`,
            noAssigned: "لا توجد مطاعم مسندة لهذا المستخدم، وبعد الإلغاء لن يصل إلى أي مطعم.",
            confirmOn: "تفعيل",
            confirmOff: "إلغاء الوصول",
            cancel: "إلغاء",
            failed: "تعذر تغيير الوصول لجميع المطاعم. حاول مرة أخرى.",
            savedOn: "تم تفعيل الوصول لجميع المطاعم.",
            savedOff: "تم إرجاع المستخدم إلى المطاعم المسندة.",
        },
    },
    en: {
        openForm: "Add user",
        closeForm: "Close form",
        editUser: (name) => `Edit: ${name}`,
        showPermissions: (name) => `Permissions for ${name}`,
        approvedLabel: "Is Approved",
        approved: "approved",
        notApproved: "disapproved",
        filterBy: (column) => `Search ${column}`,
        loading: "Loading...",
        empty: "No data found.",
        loadFailed: "Users could not be loaded.",
        retry: "Try again",
        saving: "Saving...",
        saveFailed: "The user could not be saved. Please try again.",
        permissionsFailed: "The permissions could not be saved. Please try again.",
        missing: (names) => `Complete the required fields: ${names}`,
        contactEmailHint: "A real email used for password recovery and notifications. Leave empty if the person has no personal email: recovery then goes through an administrator.",
        invalidContactEmail: "The contact email is not a valid email address.",
        globalAccess: {
            label: "Global Store Access",
            hint: "When on, the user reaches every restaurant within their role permissions. The role permissions do not change.",
            on: "Access to all restaurants is on",
            off: "Assigned restaurants only",
            enableTitle: "Enable global store access",
            disableTitle: "Disable global store access",
            enableBody: (name) => `${name} will be able to access all current and future restaurants, within their role permissions. Assigned restaurants are kept.`,
            disableBody: (name, count) => `${name} will return to their assigned restaurants only (${count}). They may immediately lose access to other restaurants they could see.`,
            noAssigned: "This user has no assigned restaurants; after disabling they will reach none.",
            confirmOn: "Enable",
            confirmOff: "Disable access",
            cancel: "Cancel",
            failed: "Global store access could not be changed. Please try again.",
            savedOn: "Global store access enabled.",
            savedOff: "The user is back to assigned restaurants only.",
        },
    },
};

const CONTACT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const textValue = (value) => (value === undefined || value === null ? '' : String(value));

export const Users = () => {
    const { lang, handleTokenExpiration } = useContext(Context);
    const { can } = useAccess();
    const t = TEXT[lang] || TEXT.en;
    const labels = translations[lang].Users;

    const [showInputs, setShowInputs] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [expandedUserId, setExpandedUserId] = useState(null);
    const [formData, setFormData] = useState(baseUser);
    const [savedData, setSavedData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [globalChange, setGlobalChange] = useState(null); // { next } while the confirmation is open
    const [notice, setNotice] = useState('');

    const [userFilterValues, setUserFilterValues] = useState(
        Array(labels.header.length).fill("")
    );

    const [userStatusFilters, setUserStatusFilters] = useState([true]);
    const userRoles = Object.keys(labels.accesses);
    const [userRoleFilters, setUserRoleFilters] = useState([]);
    const formRef = useRef(null);
    const savingRef = useRef(false);
    const requestIdRef = useRef(0);
    // Context recreates handleTokenExpiration every render.
    const tokenExpiredRef = useRef(handleTokenExpiration);
    tokenExpiredRef.current = handleTokenExpiration;

    const handleUserFilterChange = (index, value) => {
        setUserFilterValues((prev) => {
            const updated = [...prev];
            updated[index] = value;
            return updated;
        });
    };

    const filteredUsers = savedData.filter((u) => {
        // Text filters for the first 5 columns. Missing values count as empty, so
        // a user without e.g. a city is not hidden by default.
        const matchesTextFilters = [
            u.id,
            u.name,
            u.city,
            u.phone,
            [u.email, u.contactEmail].filter(Boolean).join(' '),
        ].every((field, idx) =>
            textValue(field).toLowerCase().includes(userFilterValues[idx].toLowerCase())
        );

        // Status filter (boolean)
        const matchesStatusFilter =
            userStatusFilters.length === 0 || userStatusFilters.includes(u.isAvailable);

        // Role filter
        const matchesRoleFilter =
            userRoleFilters.length === 0 || userRoleFilters.includes(u.role);

        return matchesTextFilters && matchesStatusFilter && matchesRoleFilter;
    });

    // Closing (or reopening) the form always starts a fresh create form;
    // before, closing an edit left `isUpdate` on and the next "create" tried
    // to update a user without an id.
    const toggleInputs = () => {
        if (savingRef.current) return;
        setShowInputs(prev => !prev);
        setIsUpdate(false);
        setFormData(baseUser);
        setFormError('');
    };

    const toggleExpand = (userId) => {
        setExpandedUserId(prev => (prev === userId ? null : userId));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const updateUser = (user) => {
        if (savingRef.current || !can("user.update")) return;
        setFormData({
            ...baseUser,
            ...pick(user, EDITABLE_FIELDS),
            _id: user._id,
            // Read-only context for the Global Store Access control; none of it is ever sent by the form.
            _savedRole: user.role,
            _savedContactEmail: textValue(user.contactEmail),
            _assignedCount: Array.isArray(user.between) ? user.between.length : 0,
            _globalAccess: user.globalAccess === true,
        });
        setNotice('');
        setShowInputs(true)
        setIsUpdate(true);
        setFormError('');

        setTimeout(() => {
            formRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 0);
    }

    const getUsers = async () => {
        const token = localStorage.getItem('auth-token');
        if (!token) {
            tokenExpiredRef.current();
            return;
        }
        // Only the latest request may update the list.
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const users = await fetchUsersAPI(token);
            if (requestId !== requestIdRef.current) return;
            setSavedData(Array.isArray(users) ? users : []);
            setLoadError('');
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            if (isAuthError(error)) {
                tokenExpiredRef.current();
                return;
            }
            setLoadError(error?.message || t.loadFailed);
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    };

    // The backend requires these (a missing email made signup fail with a server error). The national ID is optional now: it is a
    // legacy identification number, not an email. The contact email is optional too, but must be valid when given.
    const missingFields = () => {
        const required = isUpdate
            ? [['role', labels.selectAccess]]
            : [
                ['email', labels.email],
                ['password', labels.pass],
                ['role', labels.selectAccess],
            ];
        return required
            .filter(([field]) => !textValue(formData[field]).trim())
            .map(([, label]) => label);
    };

    const submit = async () => {
        if (savingRef.current || !can(isUpdate ? "user.update" : "user.create")) return;
        const missing = missingFields();
        if (missing.length) {
            setFormError(t.missing(missing.join(lang === 'ar' ? '، ' : ', ')));
            return;
        }
        if (textValue(formData.contactEmail).trim() && !CONTACT_EMAIL_PATTERN.test(textValue(formData.contactEmail).trim())) {
            setFormError(t.invalidContactEmail);
            return;
        }
        const token = localStorage.getItem('auth-token');
        if (!token) {
            tokenExpiredRef.current();
            return;
        }

        savingRef.current = true;
        setSaving(true);
        setFormError('');
        try {
            if (isUpdate) {
                const body = pick(formData, EDITABLE_FIELDS);
                // an untouched contact email is not resent: only a real change (including clearing it) reaches the server
                if (textValue(body.contactEmail).trim() === formData._savedContactEmail) delete body.contactEmail;
                await updateUserAPI(token, formData._id, body);
            } else {
                await signupUserAPI(token, pick(formData, CREATE_FIELDS));
            }
            setFormData(baseUser);
            setShowInputs(false);
            setIsUpdate(false);
            getUsers();
        } catch (error) {
            if (isAuthError(error)) {
                tokenExpiredRef.current();
                return;
            }
            // The form keeps what was typed.
            setFormError(error?.message || t.saveFailed);
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    // The Global Store Access control saves on its own, after a confirmation, through a dedicated endpoint.
    const canSetGlobalAccess = can("user.globalAccess.update");
    const globalEligible = isUpdate && isGlobalEligibleRole(formData._savedRole);

    const confirmGlobalChange = async () => {
        const token = localStorage.getItem('auth-token');
        if (!token) {
            tokenExpiredRef.current();
            return;
        }
        const next = globalChange.next;
        try {
            await setGlobalAccessAPI(token, formData._id, next);
        } catch (error) {
            if (error?.status === 401) {
                tokenExpiredRef.current();
                return;
            }
            throw new Error(error?.message || t.globalAccess.failed);
        }
        setFormData(prev => ({ ...prev, _globalAccess: next }));
        setGlobalChange(null);
        setNotice(next ? t.globalAccess.savedOn : t.globalAccess.savedOff);
        getUsers();
    };

    // Resolves true when saved; BtwnStores keeps its selection on failure.
    const savePermissions = async (userId, updatedBetween) => {
        if (!can("user.assignments.update")) return false;
        const token = localStorage.getItem("auth-token");
        if (!token) {
            tokenExpiredRef.current();
            return false;
        }
        try {
            await patchBetweenUserAPI(token, userId, {
                between: updatedBetween.between,
                assignedBranch: updatedBetween.assignedBranch,
            });
            getUsers();
            return true;
        } catch (error) {
            if (isAuthError(error)) {
                tokenExpiredRef.current();
                return false;
            }
            throw new Error(error?.message || t.permissionsFailed);
        }
    };

    useEffect(() => {
        getUsers();
        return () => {
            // Ignore responses that arrive after leaving the page.
            requestIdRef.current += 1;
        };
        // Load once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const columnCount = labels.header.length;

    return (
        <div className='Users'>
            <Button
                onClick={toggleInputs}
                variant="primary"
                appearance="soft"
                rounded
                disabled={saving}
                aria-label={showInputs ? t.closeForm : t.openForm}
                aria-expanded={showInputs}
                title={showInputs ? t.closeForm : t.openForm}
                icon={showInputs ? <XCircle /> : <Pencil />}
            />

            {showInputs && (
                <form
                    ref={formRef}
                    className='Users-add-container'
                    onSubmit={(e) => {
                        e.preventDefault();
                        submit();
                    }}
                    noValidate
                    aria-busy={saving || undefined}
                >
                    <div className="Users-add-inputs">
                        <Input
                            label={labels.username}
                            type="text"
                            name="name"
                            value={formData.name || ""}
                            onChange={handleChange}
                            placeholder="name"
                            fullWidth={false}
                            />
                        <Input
                            label={labels.contactEmail}
                            type="email"
                            name="contactEmail"
                            autoComplete="off"
                            value={formData.contactEmail || ""}
                            onChange={handleChange}
                            placeholder="name@example.com"
                            helperText={t.contactEmailHint}
                            fullWidth={false}
                            />
                        <Input
                            label={labels.city}
                            type="text"
                            name="city"
                            value={formData.city || ""}
                            onChange={handleChange}
                            placeholder="city"
                            fullWidth={false}
                            />
                        <Input
                            label={labels.phone}
                            type="tel"
                            name="phone"
                            value={formData.phone || ""}
                            onChange={handleChange}
                            placeholder="phone"
                            fullWidth={false}
                            />
                        {!isUpdate &&
                            (<>
                                <Input
                                    label={labels.email}
                                    required
                                    type="email"
                                    name="email"
                                    autoComplete="off"
                                    value={formData.email || ""}
                                    onChange={handleChange}
                                    placeholder="email"
                                    fullWidth={false}
                                    />
                                <Input
                                    label={labels.pass}
                                    required
                                    type="password"
                                    name="password"
                                    autoComplete="new-password"
                                    value={formData.password || ""}
                                    onChange={handleChange}
                                    placeholder="password"
                                    fullWidth={false}
                                    />
                            </>
                            )}
                        <Select
                            label={labels.header[5]}
                            value={formData.role}
                            onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                            placeholder={labels.selectAccess}
                            options={availableAccess.map((role) => ({
                                value: role,
                                label: labels.accesses[role] || role,
                            }))}
                            fullWidth={false}
                        />
                        <div className="Users-checkbox-container">
                            <Checkbox
                                checked={formData.approved || false}
                                onChange={(e) => setFormData(prev => ({ ...prev, approved: e.target.checked }))}
                                label={t.approvedLabel}
                            />
                        </div>
                        <div className="Users-checkbox-container">
                            <Checkbox
                                checked={formData.isAvailable || false}
                                onChange={(e) => setFormData(prev => ({ ...prev, isAvailable: e.target.checked }))}
                                label={labels.isAvailable}
                            />
                        </div>
                    </div>
                    {canSetGlobalAccess && globalEligible && (
                        <div className="Users-global-access">
                            <Switch
                                checked={formData._globalAccess === true}
                                onChange={(e) => setGlobalChange({ next: e.target.checked })}
                                label={`${t.globalAccess.label} / ${lang === 'ar' ? 'Global Store Access' : 'الوصول لجميع المطاعم'}`}
                                disabled={saving}
                            />
                            <p className="Users-global-access-hint">
                                {formData._globalAccess === true ? t.globalAccess.on : t.globalAccess.off}. {t.globalAccess.hint}
                            </p>
                        </div>
                    )}
                    {notice && (
                        <p className="Users-notice" role="status">{notice}</p>
                    )}
                    {formError && (
                        <p className="Users-error" role="alert">{formError}</p>
                    )}
                    <Button type="submit" fullWidth={true} disabled={saving}>
                        {saving ? t.saving : isUpdate ? labels.update : labels.send}
                    </Button>
                </form>
            )}
            {globalChange && (
                <ActionDialog
                    labels={{ confirm: globalChange.next ? t.globalAccess.confirmOn : t.globalAccess.confirmOff, cancel: t.globalAccess.cancel, failed: t.globalAccess.failed }}
                    title={globalChange.next ? t.globalAccess.enableTitle : t.globalAccess.disableTitle}
                    danger={globalChange.next}
                    onConfirm={confirmGlobalChange}
                    onClose={() => setGlobalChange(null)}
                >
                    <p>
                        {globalChange.next
                            ? t.globalAccess.enableBody(formData.name || formData.email || '')
                            : t.globalAccess.disableBody(formData.name || formData.email || '', formData._assignedCount)}
                    </p>
                    {!globalChange.next && formData._assignedCount === 0 && (
                        <NoticeBox variant="warning">{t.globalAccess.noAssigned}</NoticeBox>
                    )}
                </ActionDialog>
            )}
            <div className="table-scroll-container Users-table-scroll">

                <table className="Users-list-table-container" aria-busy={loading || undefined}>
                    <thead>
                        <tr>
                            {labels.header.map((item, i) => {
                                return (
                                    <th className="Users-list-table-header" key={i} scope="col">
                                        <p>{item}</p>

                                        {i < 5 ? (
                                            <Input
                                                value={userFilterValues[i]}
                                                onChange={(e) => handleUserFilterChange(i, e.target.value)}
                                                className="filter-input"
                                                placeholder="search"
                                                aria-label={t.filterBy(item)}
                                            />
                                        ) : null}
                                        {i === 5 ? (
                                            <div className="status-filter-dropdown">
                                                <SearchCheckList
                                                    options={userRoles.map((roleKey) => ({
                                                        value: roleKey,
                                                        label: labels.accesses[roleKey],
                                                    }))}
                                                    selected={userRoleFilters}
                                                    onChange={setUserRoleFilters}
                                                    placeholder={labels.selectAccess}
                                                />
                                            </div>
                                        ) : null}
                                        {i === 7 ? (
                                            <div className="status-filter-dropdown">
                                                <Select
                                                    value={String(userStatusFilters[0])}
                                                    onChange={(e) => setUserStatusFilters([e.target.value === "true"])}
                                                    placeholder={translations[lang].restaurants.selectStatus}
                                                    options={[
                                                        {
                                                            value: "true",
                                                            label: labels.available,
                                                        },
                                                        {
                                                            value: "false",
                                                            label: labels.unavailable,
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        ) : null}

                                    </th>
                                );
                            })}
                        </tr>


                    </thead>
                    <tbody>
                        {loading && savedData.length === 0 ? (
                            <tr>
                                <td className="Users-list-table-p Users-state" colSpan={columnCount} role="status">{t.loading}</td>
                            </tr>
                        ) : loadError && savedData.length === 0 ? (
                            <tr>
                                <td className="Users-list-table-p Users-state" colSpan={columnCount}>
                                    <span role="alert">{loadError}</span>{" "}
                                    <Button size="sm" appearance="soft" onClick={getUsers}>{t.retry}</Button>
                                </td>
                            </tr>
                        ) : filteredUsers.length === 0 ? (
                            <tr>
                                <td className="Users-list-table-p Users-state" colSpan={columnCount}>{t.empty}</td>
                            </tr>
                        ) : (
                            filteredUsers.map((item, index) => {
                                const rowKey = item._id || item.id || index;
                                const displayName = item.name || item.email || item.id || '';
                                const expanded = expandedUserId === rowKey;
                                return (
                                    <React.Fragment key={rowKey}>
                                        <tr>
                                            <td className="Users-list-table-p"><p>{item.id}</p></td>
                                            <td className="Users-list-table-p"><p>{item.name}</p></td>
                                            <td className="Users-list-table-p"><p>{item.city}</p></td>
                                            <td className="Users-list-table-p"><p>{item.phone}</p></td>
                                            <td className="Users-list-table-p"><p>{item.email}</p>{item.contactEmail && item.contactEmail !== item.email ? <p className="Users-contact-email" title={labels.contactEmail}>{item.contactEmail}</p> : null}</td>
                                            <td className="Users-list-table-p"><p>{labels.accesses[item.role] || item.role}</p></td>
                                            <td className="Users-list-table-p clickable">
                                                <button
                                                    type="button"
                                                    className="Users-cell-button"
                                                    aria-expanded={expanded}
                                                    aria-label={t.showPermissions(displayName)}
                                                    onClick={() => toggleExpand(rowKey)}
                                                >
                                                    <span className="Users-pill">{item.between?.length || 0}</span>
                                                </button>
                                            </td>
                                            <td className={`Users-list-table-p available ${item.isAvailable ? '' : 'yellow'}`}><p>{item.isAvailable ? labels.available : labels.unavailable}</p></td>
                                            <td className={`Users-list-table-p available ${item.approved ? '' : 'yellow'}`}><p>{item.approved ? t.approved : t.notApproved}</p></td>
                                            <td className="Users-list-table-p update">
                                                <Button
                                                    variant="primary"
                                                    appearance="ghost"
                                                    size="sm"
                                                    rounded
                                                    icon={<Pencil />}
                                                    disabled={saving}
                                                    aria-label={t.editUser(displayName)}
                                                    title={labels.update}
                                                    onClick={() => updateUser(item)}
                                                />
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr>
                                                <td colSpan={columnCount}>
                                                    {/* Keyed by user: a different user never inherits
                                                        this panel's unsaved selection. */}
                                                    <BtwnStores
                                                        key={rowKey}
                                                        between={item.between || []}
                                                        assignedBranch={item.assignedBranch || []}
                                                        onUpdate={(updatedBetween) => savePermissions(item._id, updatedBetween)}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
